/**
 * Browser-side image normalisation pipeline.
 *
 * Accepts anything the user picks (HEIC from iPhone, huge Android JPEG,
 * PNG screenshot, WebP, whatever) and returns a JPEG File no larger than
 * a ~2048px longest edge. The user never sees a format or size error —
 * every failure falls back to uploading the raw bytes unchanged.
 *
 * Both heavy deps are dynamically imported so a user who never uploads
 * an image pays zero bundle cost.
 *
 * Used by client photos and by the salon settings uploads (logo, gallery,
 * email header). Lives in shared/ because it is cross-feature.
 */

const HEIC_TYPES = new Set(['image/heic', 'image/heif']);
const HEIC_EXTS = /\.(heic|heif)$/i;

const isHeic = (file) => {
    if (!file) return false;
    if (HEIC_TYPES.has((file.type || '').toLowerCase())) return true;
    // iOS Safari sometimes leaves the type blank for HEIC picks
    if (!file.type && HEIC_EXTS.test(file.name || '')) return true;
    return false;
};

const toJpegFile = (blob, originalName) => {
    const baseName = (originalName || 'photo').replace(/\.[^.]+$/, '');
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
};

export async function normaliseImage(file) {
    if (!file) return file;

    let working = file;

    // Step 1: HEIC → JPEG. Only loaded if the user actually picks a HEIC,
    // so the common Android/desktop case stays lean.
    if (isHeic(working)) {
        try {
            const { default: heic2any } = await import('heic2any');
            const converted = await heic2any({
                blob: working,
                toType: 'image/jpeg',
                quality: 0.9,
            });
            const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
            working = toJpegFile(jpegBlob, working.name);
        } catch {
            // heic2any failed — hand the raw file to the server and let it cope.
            return file;
        }
    }

    // Step 2: resize, fix EXIF orientation, re-encode to JPEG ~0.85.
    try {
        const { default: imageCompression } = await import('browser-image-compression');
        const compressed = await imageCompression(working, {
            maxSizeMB: 1,
            maxWidthOrHeight: 2048,
            useWebWorker: true,
            fileType: 'image/jpeg',
            initialQuality: 0.85,
            // browser-image-compression preserves EXIF orientation by default
            // and strips other metadata — matches what we want.
        });
        // imageCompression returns a File when the input was a File, but
        // the type field can come back as the browser's guess. Force a name
        // + jpeg type so the upload is unambiguous.
        return toJpegFile(compressed, working.name);
    } catch {
        // Fall back to whatever we had after step 1 — still better than
        // surfacing an error to the user.
        return working;
    }
}
